import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { HttpClient, HttpResponse, HttpHeaders } from "@angular/common/http";

import { ApiService } from "../core/api.service";
import { PublicationModel, PublicationNode } from "./publication";
import {
  ComparisonDef,
  JurisdictionResult,
} from "../regcompare/statediff-model";
import {
  SimpleSearchResult,
  SearchRequest,
} from "../apptool/model/TemplateList";

@Injectable({
  providedIn: "root",
})
export class PublicationService {
  public loading = false;
  public selectState = "None";

  constructor(
    private apiService: ApiService,
    protected http: HttpClient,
  ) {}

  public getPublication(
    userId: number,
    category: string,
  ): Observable<PublicationModel[]> {
    let URL = "Publication/GetList/" + userId + "/" + category;
    return this.apiService.get(URL);
  }

  public getPublications(userId: number): Observable<PublicationModel[]> {
    let URL = "Publication/GetList/" + userId;
    return this.apiService.get(URL);
  }

  public getAuthorizedJurisdiction(): Observable<JurisdictionResult[]> {
    let URL = "search/GetJurisdiction";
    return this.apiService.get(URL);
  }

  /**
   * Call backend to search publications.
   * @param userId - who initials the search, the result list will only include the pub the user subscribed.
   * @param keystring - the key word for searching.
   * @returns
   */
  public searchPublication(
    userId: number,
    keystring: string,
  ): Observable<PublicationModel[]> {
    let URL = "Publication/SearchPub/" + userId + "/" + keystring;
    return this.apiService.get(URL);
  }

  // Sent email to info@stpub.com for purchasing product.
  public sendEmail(message: string) {
    let URL = "Utility/SendEmail";
    return this.apiService.post(URL, {
      emailTo: "info@stpub.com",
      subject: "Product purchasing",
      message: message,
    });
  }

  public getSelectedState(): string {
    return this.selectState;
  }

  public setSelectedState(st: string): void {
    this.selectState = st;
  }

  decodeHtml(html: string) {
    if (html == null) return null;
    var txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
  }

  public querySimpleTextSearch(
    searchText: string,
    jurisdictions: JurisdictionResult[],
    topics: string,
    offset: number = 0,
    limit: number = 20,
    questionType?: number,
  ): Observable<SimpleSearchResult[]> {
    var targetURL = "Search/SimpleSearch";
    let searchRequest: SearchRequest = {
      terms: searchText,
      jurisdictions: JSON.stringify(jurisdictions),
      topics: topics,
      offset: offset,
      limit: limit,
      questionType: questionType,
    };
    return this.apiService.post(targetURL, searchRequest);
  }

  public saveSearchDefinition(
    searchName: string,
    searchText: string,
    jurisdictions: JurisdictionResult[],
    topics: string,
    questionType?: number,
    id?: number,
  ): Observable<any> {
    var targetURL = id && id > 0 ? `Search/saved/${id}` : "Search/saved";
    let searchRequest: SearchRequest = {
      searchName: searchName,
      terms: searchText,
      jurisdictions: JSON.stringify(jurisdictions),
      topics: topics,
      offset: 0, // Not used for saving, but required by interface
      limit: 0, // Not used for saving, but required by interface
      questionType: questionType,
    };
    return id && id > 0
      ? this.apiService.put(targetURL, searchRequest)
      : this.apiService.post(targetURL, searchRequest);
  }

  public getSavedSearchDefinitions(): Observable<any[]> {
    var targetURL = "search/saved";
    return this.apiService.get(targetURL);
  }

  public deleteSearchDefinition(searchId: number): Observable<any> {
    var targetURL = "search/saved/" + searchId;
    return this.apiService.delete(targetURL);
  }

  // Get the publication title by acronym for the latest version.
  public queryTitle(acronym: string) {
    var targetURL = "Publication/GetTitle/" + acronym;
    return this.apiService.get(targetURL);
  }

  public getChild(
    parentString: string,
    parentKey: number,
    level: number,
  ): Observable<PublicationNode[]> {
    var targetURL = "Publication/";
    if (level == 0) {
      targetURL = targetURL + "GetTopic/" + parentString + "/" + parentKey;
    } else if (level == 1) {
      targetURL = targetURL + "GetRulebook/" + parentKey;
    } else if (level == 2) {
      targetURL =
        targetURL + "GetSection/" + parentKey + "/" + this.getSelectedState();
    } else if (level == 3) {
      targetURL =
        targetURL + "GetParagraph/" + parentKey + "/" + this.getSelectedState();
    }
    return this.apiService.get(targetURL);
  }

  public getGuidenote(paraKey: number) {
    var targetURL = "Publication/GetGuidenote/" + paraKey;
    return this.apiService.get(targetURL);
  }

  showLoadingAnimation(showFlag: boolean) {
    this.loading = showFlag;
  }

  public getFileContent(filePath: string) {
    return this.http.get(filePath, { responseType: "text" });
  }

  public getPdfUrl(pubid: string, version: string) {
    var targetURL = "Publication/GetPdf/" + pubid + "/" + version.trim();
    return this.apiService.get(targetURL);
  }

  downloadFile(filePath: string) {
    let headers = new HttpHeaders(); //.set('authorization','Bearer '+token);
    return this.http.get(filePath, { headers: headers, responseType: "blob" });
  }

  // Do the comparison
  public regCompare(states: string[], rbids: string[]) {
    let stateString = states.join("|");
    let rbidString = rbids.join("|");
    var targetURL = "Publication/RegCompare/" + stateString + "/" + rbidString;
    return this.apiService.get(targetURL);
  }

  // Get the comparison definition.
  public getRegComparison(userId: string) {
    let targetURL = "Apptool/GetComparison/" + userId;
    return this.apiService.get(targetURL);
  }

  // Delete the comparison definition.
  public DeleteRegComparison(cId: string) {
    let targetURL = "Apptool/DeleteComparison/" + cId;
    return this.apiService.delete(targetURL);
  }

  // Save comparison definition.
  public saveComparison(comparisonDef: ComparisonDef) {
    let URL = "Apptool/SaveComparison";
    return this.apiService.post(URL, comparisonDef);
  }

  // Save the aciont on publication.
  public markPublicationView(
    pubId: number,
    version: string,
    format: string,
    title: string,
    acronym: string,
  ) {
    let URL = "Publication/TouchPublication";
    const index = acronym.indexOf("|");
    if (index != -1) {
      acronym = acronym.substring(0, index);
    }

    return this.apiService.post(URL, {
      pubid: pubId,
      version: version,
      format: format,
      title: title,
      acronym: acronym,
      userid: 0,
    });
  }
}
